import { useSession } from "@saas/auth/hooks/use-session";
import { useCredits } from "@saas/credits/hooks/use-credits";
import { useRouter } from "@shared/hooks/router";
import { useLocalStorage } from "@shared/hooks/use-local-storage";
import { apiClient } from "@shared/lib/api-client";
import {
	clearBlobPendingState,
	saveBlobPendingState,
	takeBlobPendingState,
} from "@shared/lib/pending-state";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import type { AIModel, GeneratedPrompt, PromptLanguage } from "../types";

export type BatchPromptResult = {
	imageId: string;
	prompt: string;
	originalPrompt?: string;
	error?: string;
};

const PENDING_BATCH_IMAGE_TO_PROMPT_STATE_KEY =
	"pendingBatchImageToPromptState";

export function useBatchImageToPrompt() {
	const [imageFiles, setImageFiles] = useState<File[]>([]);
	const [selectedModel, setSelectedModel] = useState<AIModel>("flux");
	const [promptLanguage, setPromptLanguage] = useState<PromptLanguage>("en");
	const [results, setResults] = useState<BatchPromptResult[]>([]);
	const [promptHistory, setPromptHistory] = useState<GeneratedPrompt[]>([]);
	const [processingStatus, setProcessingStatus] = useState<
		"idle" | "processing" | "completed" | "error"
	>("idle");
	const { savePrompt } = useLocalStorage();
	const { user } = useSession();
	const router = useRouter();
	const { getRemainingCredits, hasUnlimitedCredits, refetchCredits } =
		useCredits();
	const hasRestoredStateRef = useRef(false);

	// Convert image to Base64
	const convertImageToBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				const base64String = reader.result as string;
				const base64 = base64String.split(",")[1];
				resolve(base64);
			};
			reader.onerror = (error) => reject(error);
		});
	};

	const convertFileToDataUrl = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = (error) => reject(error);
		});
	};

	const persistStateForLogin = useCallback(async () => {
		if (typeof window === "undefined") return;

		try {
			// File 直接落 IndexedDB：不用转 data URL，也就没有了那个
			// 「超 4.5MB 就把图全丢掉」的兜底
			await saveBlobPendingState(
				PENDING_BATCH_IMAGE_TO_PROMPT_STATE_KEY,
				{
					images: imageFiles.map((file) => ({
						blob: file,
						name: file.name,
					})),
					selectedModel,
					promptLanguage,
				},
			);
		} catch (error) {
			console.error(
				"Failed to persist batch-image-to-prompt state:",
				error,
			);
		}
	}, [imageFiles, selectedModel, promptLanguage]);

	// Restore state from localStorage after login
	useEffect(() => {
		if (typeof window === "undefined") return;
		if (!user) return;
		if (hasRestoredStateRef.current) return;

		hasRestoredStateRef.current = true;

		// If state already exists in-memory, clear to avoid stale restores.
		if (imageFiles.length > 0) {
			void clearBlobPendingState(PENDING_BATCH_IMAGE_TO_PROMPT_STATE_KEY);
			return;
		}

		void takeBlobPendingState<{
			images?: { blob: Blob; name: string }[];
			selectedModel?: AIModel;
			promptLanguage?: PromptLanguage;
		}>(PENDING_BATCH_IMAGE_TO_PROMPT_STATE_KEY, {
			maxAgeMs: 3600000,
		}).then((state) => {
			if (!state) {
				return;
			}

			if (state.selectedModel) setSelectedModel(state.selectedModel);
			if (state.promptLanguage) setPromptLanguage(state.promptLanguage);

			if (state.images?.length) {
				setImageFiles(
					state.images.map(
						(image) =>
							new File([image.blob], image.name, {
								type: image.blob.type,
							}),
					),
				);
			}
		});
	}, [user, imageFiles.length]);

	// Handle image selection
	const handleImagesSelected = (files: File[]) => {
		setImageFiles(files);
	};

	// Remove a specific image
	const removeImage = (index: number) => {
		setImageFiles((prev) => prev.filter((_, i) => i !== index));
	};

	// Clear all images
	const clearImages = () => {
		setImageFiles([]);
	};

	// Generate prompt for a single image
	const generatePromptMutation = useMutation({
		mutationFn: async (requestData: {
			imageUrl?: string;
			imageBase64?: string;
			model: AIModel;
			language: PromptLanguage;
			imageId: string;
		}) => {
			if (!requestData.imageUrl && !requestData.imageBase64) {
				throw new Error(
					"Either imageUrl or imageBase64 must be provided",
				);
			}

			const response = await apiClient["image-to-prompt"].$post({
				json: {
					imageUrl: requestData.imageUrl,
					imageBase64: requestData.imageBase64,
					model: requestData.model,
					language: requestData.language,
				},
			});

			if (!response.ok) {
				let errorMessage = "Failed to generate prompt";
				try {
					const errorText = await response.text();
					const error = JSON.parse(errorText);
					errorMessage = error.message || errorMessage;
				} catch {
					// Use default error message if parsing fails
				}
				throw new Error(errorMessage);
			}

			const data = await response.json();
			return {
				...data,
				imageId: requestData.imageId,
			};
		},
	});

	// Process all images in batch
	const processBatch = async () => {
		if (generatePromptMutation.isPending) return;

		// Check if user is authenticated
		if (!user) {
			await persistStateForLogin();

			if (typeof window !== "undefined") {
				window.dispatchEvent(
					new CustomEvent("show-login", {
						detail: {
							redirectTo:
								window.location.pathname +
								window.location.search,
						},
					}),
				);
				return;
			}

			const returnUrl = "/batch-image-to-prompt";
			router.push(
				`/auth/login?redirectTo=${encodeURIComponent(returnUrl)}`,
			);
			return;
		}

		// Check if there are any images to process
		if (imageFiles.length === 0) {
			toast.error("Please upload at least one image");
			return;
		}

		// Check for sufficient credits
		const isUnlimited = hasUnlimitedCredits("IMAGE_TO_TEXT");
		const remainingCredits = getRemainingCredits("IMAGE_TO_TEXT");

		if (!isUnlimited && remainingCredits < imageFiles.length) {
			toast.error(
				`You need ${imageFiles.length} credits but only have ${remainingCredits}`,
			);
			return;
		}

		// Reset results and set processing state
		setResults([]);
		setProcessingStatus("processing");

		try {
			// Process each image sequentially to avoid rate limiting
			const newResults: BatchPromptResult[] = [];
			const newHistoryItems: GeneratedPrompt[] = [];

			for (let i = 0; i < imageFiles.length; i++) {
				const file = imageFiles[i];
				const imageId = `image-${i}`;

				try {
					const base64 = await convertImageToBase64(file);

					// Call the API
					const result = await generatePromptMutation.mutateAsync({
						imageBase64: base64,
						model: selectedModel,
						language: promptLanguage,
						imageId,
					});

					// Save result
					const promptResult: BatchPromptResult = {
						imageId,
						prompt: result.prompt,
						originalPrompt: result.originalPrompt,
					};

					newResults.push(promptResult);

					// Save to IndexedDB
					await savePrompt(
						result.prompt,
						result.originalPrompt,
						base64,
						selectedModel,
					);

					// Add to history
					const historyItem: GeneratedPrompt = {
						model: selectedModel,
						prompt: result.prompt,
						originalPrompt: result.originalPrompt,
						language: promptLanguage,
						timestamp: new Date(),
					};

					newHistoryItems.push(historyItem);

					// Update results as we go
					setResults([...newResults]);
				} catch (error) {
					console.error(`Error processing image ${i}:`, error);

					// Add error to results
					newResults.push({
						imageId,
						prompt: "",
						error:
							error instanceof Error
								? error.message
								: "Unknown error",
					});

					setResults([...newResults]);
				}
			}

			// Update history with all new items
			setPromptHistory((prev) => [...newHistoryItems, ...prev]);
			setProcessingStatus("completed");
			// Credits are consumed server-side; refresh local cache
			void refetchCredits();
		} catch (error) {
			console.error("Error processing batch:", error);
			toast.error("Failed to process images");
			setProcessingStatus("error");
		}
	};

	// Get remaining credits formatted (with special handling for unlimited)
	const getRemainingCreditsFormatted = () => {
		if (hasUnlimitedCredits("IMAGE_TO_TEXT")) {
			return "infinity"; // Special marker for infinite credits
		}
		return getRemainingCredits("IMAGE_TO_TEXT");
	};

	return {
		imageFiles,
		selectedModel,
		promptLanguage,
		results,
		promptHistory,
		processingStatus,
		usesRemaining: getRemainingCreditsFormatted(),
		hasUnlimitedCredits: hasUnlimitedCredits("IMAGE_TO_TEXT"),
		isPending: generatePromptMutation.isPending,
		handleImagesSelected,
		removeImage,
		clearImages,
		processBatch,
		setSelectedModel,
		setPromptLanguage,
		setPromptHistory,
		user,
	};
}
